import {
  Badge, Button, Card, EmptyState, Notice, PageHeader, Row, Stack, useApi, useI18n, useOffline,
} from '@daghfal/core';
import { useQueries } from '@tanstack/react-query';
import { childName } from '../names';
import { useQueue, useQueueEntries } from '../offline/QueueProvider';

/**
 * What is still waiting to be sent (FR-STF-09).
 *
 * The requirement is that no entry is lost silently and that the user is told when items are pending.
 * This screen is the second half of that: every queued entry is listed with what it was and when it was
 * recorded, so a teacher can see for themselves that the morning's work is safe.
 */
export function Queue() {
  const { t, formatDateTime } = useI18n();
  const queue = useQueue();
  const entries = useQueueEntries();
  const offline = useOffline();

  if (entries.length === 0) {
    return (
      <>
        <PageHeader title={t('queue.title')} />
        <EmptyState icon="✅" title={t('queue.empty')} />
      </>
    );
  }

  return (
    <Stack>
      <PageHeader title={t('queue.title')} />

      <Notice tone={offline ? 'warning' : 'info'} title={t('queue.pending', { count: entries.length })}>
        {t('queue.hint')}
      </Notice>

      <Row>
        <Button variant="secondary" onClick={() => void queue.flush()} disabled={offline}>
          {t('queue.retry')}
        </Button>
      </Row>

      <Stack>
        {entries.map((entry) => (
          <Card key={entry.id}>
            <Row between>
              <span className="dg-stack dg-stack--tight">
                <strong>{t(`action.${entry.kind}`)}</strong>
                <span className="dg-card__hint">{formatDateTime(entry.recordedAt)}</span>
                {entry.childIds.length > 1 && (
                  <span className="dg-card__hint">{t('bulk.selected', { count: entry.childIds.length })}</span>
                )}
                {/* The API's own words, which say what to do next (NFR-USE-05). */}
                {entry.refusal && <span className="dg-card__hint">{entry.refusal}</span>}
                {/* Who it still waits for, by name: an entry recorded for some of its children is held for the
                    others (NR-9), and the teacher has to know whom to check in. */}
                {entry.waitingFor && <HeldFor childIds={entry.waitingFor} />}
              </span>

              {/* A rejected entry is the only one a teacher has to do anything about, so it is the only
                  one that offers a control. Everything else is simply waiting. */}
              {entry.rejected ? (
                <Row>
                  <Badge tone="danger">{t('error.title')}</Badge>
                  <Button variant="secondary" onClick={() => queue.revive(entry.id)}>
                    {t('error.retry')}
                  </Button>
                  <Button variant="ghost" onClick={() => queue.discard(entry.id)}>
                    {t('action.cancel')}
                  </Button>
                </Row>
              ) : (
                <Badge tone="warning">⏳</Badge>
              )}
            </Row>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

/** The names of the children a held entry is for, as far as they can be read (not offline). */
function HeldFor({ childIds }: { childIds: number[] }) {
  const api = useApi();
  const { t } = useI18n();
  const children = useQueries({
    queries: childIds.map((id) => ({ queryKey: ['child', id], queryFn: () => api.children.get(id) })),
  });

  const names = children.flatMap((query) => (query.data ? [childName(query.data)] : []));
  if (names.length === 0) return null;

  return <span className="dg-card__hint">{t('queue.heldFor', { names: names.join('، ') })}</span>;
}
